import type React from "react";
import { Tooltip } from "antd";
import { LockOutlined } from "@ant-design/icons";
import type { GridWidgetType } from "../types";
import { getWidgetTemplates } from "../widgetRegistry";

export interface WidgetTemplate {
  type: GridWidgetType;
  label: string;
  description: string;
  icon: React.ReactNode;
}

interface TemplatePickerProps {
  onSelect: (template: WidgetTemplate) => void;
  availability?: Record<string, boolean>;
}

// A widget is usable only when its availability flag is explicitly true.
// A present-but-unset flag (false or missing key) renders the card locked.
// A completely absent map (undefined/null, e.g. team info not yet loaded)
// stays permissive so the whole picker isn't locked during load.
export function isLocked(
  type: GridWidgetType,
  availability?: Record<string, boolean>,
): boolean {
  return availability != null && availability[type] !== true;
}

// The core widgets always lead the picker, in this fixed order. Anything else
// keeps its registry order behind them.
const PRIORITY_ORDER: GridWidgetType[] = [
  "chart",
  "table",
  "title",
  "logo",
  "spacer",
];

function priorityRank(type: GridWidgetType): number {
  const index = PRIORITY_ORDER.indexOf(type);
  return index === -1 ? PRIORITY_ORDER.length : index;
}

export function TemplatePicker({ onSelect, availability }: TemplatePickerProps) {
  // Available widgets sort ahead of locked ones; the core widgets lead each
  // group in PRIORITY_ORDER, and ties keep registry order (stable sort).
  const templates = [...getWidgetTemplates()].sort((a, b) => {
    const lockedA = isLocked(a.type, availability) ? 1 : 0;
    const lockedB = isLocked(b.type, availability) ? 1 : 0;
    if (lockedA !== lockedB) return lockedA - lockedB;
    return priorityRank(a.type) - priorityRank(b.type);
  });

  return (
    <>
      <p className="awm-step-subtitle">Pick your goal template</p>
      <div className="awm-template-grid">
        {templates.map((t) => {
          const locked = isLocked(t.type, availability);
          const card = (
            <button
              key={t.type}
              className={`awm-template-card${locked ? " awm-template-card-locked" : ""}`}
              type="button"
              onClick={locked ? undefined : () => onSelect(t)}
              aria-disabled={locked}
            >
              {locked && (
                <span className="awm-template-lock">
                  <LockOutlined />
                </span>
              )}
              <span className="awm-template-icon">{t.icon}</span>
              <span className="awm-template-label">{t.label}</span>
              <span className="awm-template-desc">{t.description}</span>
            </button>
          );
          return locked ? (
            <Tooltip key={t.type} title="Not enabled for your team">
              {card}
            </Tooltip>
          ) : (
            card
          );
        })}
      </div>
    </>
  );
}
